import React, { useState } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * UnitPricePromptModal
 *
 * Shown before a recording modal when the farmer or client does not have a
 * saved unit price. The user can accept the default value or enter a custom
 * one before proceeding to the main recording form.
 *
 * Props:
 *  - name        {string}   Farmer / client display name
 *  - type        {"farmer"|"client"}  Controls label wording
 *  - defaultPrice {number}  Default price to pre-fill
 *  - onConfirm   {(price: number) => void}  Called when user confirms
 *  - onCancel    {() => void}              Called when user cancels
 */
const UnitPricePromptModal = ({ name, type, defaultPrice, onConfirm, onCancel }) => {
  const [price, setPrice] = useState(String(defaultPrice));

  const label = type === "farmer" ? "Cost per Kg (KES)" : "Unit Price per Liter (KES)";
  const priceUnit = type === "farmer" ? "KES/Kg" : "KES/L";
  const profileField = type === "farmer" ? "cost per kg" : "unit price";

  const handleConfirm = () => {
    const parsed = parseFloat(price);
    if (!parsed || parsed <= 0) return;
    onConfirm(parsed);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-amber-700 flex items-center gap-2">
            <AlertTriangle size={22} />
            No Unit Price Found
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={22} />
          </button>
        </div>

        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 font-medium mb-1">
            {name} does not have a saved {profileField}.
          </p>
          <p className="text-sm text-amber-700">
            The default value of <strong>KES {defaultPrice} {priceUnit}</strong> will be used.
            You can change it below before proceeding. To avoid this prompt in
            future, update the {profileField} in {name}&apos;s profile.
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label} *
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            placeholder={`e.g. ${defaultPrice}`}
            autoFocus
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!parseFloat(price) || parseFloat(price) <= 0}
            className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition disabled:bg-amber-300"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnitPricePromptModal;
